/**
 * Talking to the account API.
 *
 * Thin, like `figureService`, and for the same reason: everything that can be got wrong without a
 * network belongs on the server, and what is left here is one call per endpoint plus the
 * discipline of never throwing at a component. Every failure is a message somebody can read,
 * because the caller is a dialogue with somewhere to put one.
 *
 * `fetch` itself — credentials, the CSRF token, how a refusal is phrased — lives in `apiCall`,
 * because the token is shared state and a second copy of it would be wrong from the first login
 * onwards. See the reasoning there.
 */
import { call, type Failure, isFailure } from './apiCall';

export interface Account {
  readonly id: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly createdAt?: string;
}

export interface Wording {
  readonly text: string;
  readonly version: string;
}

export interface Me {
  readonly user: Account | null;
  readonly marketingConsent: boolean;
  /** The exact words shown beside the consent box. Comes from the server so the two cannot drift. */
  readonly wording: Wording | null;
}

export { type Failure, isFailure };

/**
 * Who is signed in, and the token every later request needs.
 *
 * Called on load. A `401` here is not an error — it is the answer "nobody", and it still carries
 * a CSRF token, because the sign-in form that follows it is exactly what needs one.
 */
export async function readMe(): Promise<Me> {
  const result = await call<{
    user: Account;
    marketingConsent: boolean;
    wording: Wording;
  }>('/v1/me');

  if (isFailure(result)) return { user: null, marketingConsent: false, wording: null };
  return {
    user: result.user,
    marketingConsent: result.marketingConsent,
    wording: result.wording,
  };
}

export function requestCode(email: string): Promise<{ sent: true } | Failure> {
  return call('/v1/auth/code', { body: { email } });
}

export function verifyCode(
  email: string,
  code: string,
  marketingConsent: boolean
): Promise<{ user: Account } | Failure> {
  return call('/v1/auth/verify', { body: { email, code, marketingConsent } });
}

export function setMarketingConsent(marketing: boolean): Promise<{ marketing: boolean } | Failure> {
  return call('/v1/me/consent', { body: { marketing } });
}

export function signOut(): Promise<{ ok: true } | Failure> {
  return call('/v1/auth/logout', { body: {} });
}

export function signOutEverywhere(): Promise<{ ended: number } | Failure> {
  return call('/v1/auth/logout-all', { body: {} });
}

export function deleteAccount(): Promise<{ deleted: true } | Failure> {
  return call('/v1/me', { method: 'DELETE', body: {} });
}

/** Everything held about this account, as a file the browser downloads. */
export async function exportMyData(): Promise<Failure | null> {
  const result = await call<Record<string, unknown>>('/v1/me/export');
  if (isFailure(result)) return result;

  const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'du-lieu-hinhhoc-vn.json';
  link.click();
  // Released on the next tick: revoking immediately can cancel the download in some browsers.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
  return null;
}
